import React, { useEffect, createElement } from 'react';
import { Globe } from 'lucide-react';
// TypeScript declarations for Google Translate
declare global {
  interface Window {
    google: {
      translate: {
        TranslateElement: new (config: {
          pageLanguage: string;
          includedLanguages: string;
          autoDisplay: boolean;
        }, elementId: string) => void;
      };
    };
    googleTranslateElementInit?: () => void;
  }
}
export const GoogleTranslateWidget: React.FC = () => {
  useEffect(() => {
    // Only proceed if the script hasn't been initialized globally
    if (window.googleTranslateElementInit) {
      return;
    }
    // Define the initialization function globally
    window.googleTranslateElementInit = function () {
      new window.google.translate.TranslateElement({
        // ALWAYS set source page language to ENGLISH ('en')
        pageLanguage: 'en',
        // Offer all target languages for users to choose from
        includedLanguages: 'pt,es,ja,ko,de,fr,hi,id',
        autoDisplay: false
      }, 'google_translate_element');
    };
    // Load the Google Translate script
    const script = document.createElement('script');
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
    // Inject custom styles to polish Google's widget
    const style = document.createElement('style');
    style.textContent = `
      /* Hide Google Translate banner and branding */
      .goog-te-banner-frame,
      .goog-te-balloon-frame,
      .skiptranslate iframe {
        display: none !important;
      }
      
      body {
        top: 0 !important;
      }

      /* Style the Google Translate dropdown */
      .goog-te-combo {
        width: 100%;
        padding: 0.625rem 0.75rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
        background-color: white;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        outline: none;
        transition: all 0.2s;
        cursor: pointer;
        font-family: inherit;
      }

      .goog-te-combo:hover {
        border-color: #d1d5db;
        background-color: #f9fafb;
      }

      .goog-te-combo:focus {
        border-color: #dc2626;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
      }

      /* Style the gadget container */
      .goog-te-gadget {
        font-family: inherit !important;
        color: inherit !important;
      }

      .goog-te-gadget .goog-te-combo {
        margin: 0 !important;
      }

      /* Hide "Powered by" text */
      .goog-te-gadget-simple .goog-te-menu-value span:first-child {
        display: none;
      }

      /* Improve dropdown appearance */
      .goog-te-menu-frame {
        border-radius: 0.75rem !important;
        box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
        border: 1px solid #e5e7eb !important;
      }

      .goog-te-menu2 {
        border: none !important;
        border-radius: 0.75rem !important;
      }

      .goog-te-menu2-item div,
      .goog-te-menu2-item:link div,
      .goog-te-menu2-item:visited div,
      .goog-te-menu2-item:active div {
        color: #374151 !important;
        font-weight: 500 !important;
        padding: 0.5rem 1rem !important;
        font-size: 0.875rem !important;
      }

      .goog-te-menu2-item-selected div,
      .goog-te-menu2-item:hover div {
        background-color: #fef2f2 !important;
        color: #dc2626 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[src*="translate.google.com"]');
      if (existingScript) {
        existingScript.remove();
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);
  return <div className="w-full bg-gradient-to-br from-stone-50 to-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-1106">
      <div className="flex items-start gap-3" data-id="element-1107">
        <div className="flex-shrink-0 mt-0.5" data-id="element-1108">
          <div className="w-9 h-9 bg-white rounded-lg border border-gray-200 flex items-center justify-center shadow-sm" data-id="element-1109">
            <Globe className="w-4 h-4 text-gray-600" strokeWidth={2.5} data-id="element-1110" />
          </div>
        </div>

        <div className="flex-1 min-w-0" data-id="element-1111">
          <div className="flex items-center gap-2 mb-1" data-id="element-1112">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-1113">
              Need another language?
            </h3>
          </div>

          <p className="text-xs text-gray-600 mb-3 leading-relaxed" data-id="element-1114">
            Use Google Translate to view this page in 100+ languages
          </p>

          <div id="google_translate_element" className="w-full" role="region" aria-label="Language translation widget" data-id="element-1115" />
        </div>
      </div>
    </div>;
};
// Support both named and default imports
export default GoogleTranslateWidget;